import {useEffect, useMemo, useState} from 'react';
import TrackingContext from "./trackingContext";
import TagManager from 'react-gtm-module';
import Cookies from 'js-cookie';
import {md5} from '@tools/md5';
import { v4 as uuidv4 } from 'uuid';
import { getCartItemPrices } from 'functions/parseSWPrices';
import { getContactId, getDiscoveryKey, getUserSession } from '@tools/elevateClient';
import Head from "next/head";
import fetch from "react-storefront/fetch";
import useApplicationStoreStore from '@services/stores/ApplicationStore';
import useCartStore from '@services/stores/CartStore';
import useSessionStore from '@services/stores/SessionStore';
import useUserStore from '@services/stores/UserStore';
import { useRouter } from 'next/router';
import { getProductAnalytics } from '@tools/productAnalytics';


export default function TrackingProvider({gtmId, pageProps, children}) {
	const router = useRouter();
	const { isDevelopment } = useApplicationStoreStore(state => state.methods);
	const myNewportService = useUserStore((state) => state.service);
	const session = useSessionStore((state) => state.session);
	const cart = useCartStore((state) => state.cart);
	const { refresh } = useCartStore(state => state.methods);
	const {getEclubData} = useSessionStore((state) => state.methods);
	const [eclubData, setEclubData] = useState(getEclubData());
	const [trackingInitiated, setTrackingInitiated] = useState(false)
	const {config} = session;
	let timeout = setTimeout(() => {}, 1);

	const tagManagerArgs = {
		gtmId: gtmId
	}

	/* Tracking queue logic */
	let queue = [];

	function convertToSnakeCase(input) {
		if (typeof input !== 'string') return input;
		return input.replace(/ /g, '_').toLowerCase();
	}

	function navigationType() {
		const isSoftLoad = window?.__lazyHydrateNavigated;
		return isSoftLoad ? "soft" : "hard";
	}

	function confirmEventDependency(dependency, values) {
		switch (dependency) {
			case 'cookie':
				let cookiesNotFound = false;

				values.forEach((value) => {
					if(!Cookies.get(value)) {
						cookiesNotFound = true;
					}
				});

				return !cookiesNotFound;
			case false:
			default:
				return true;
		}
	}

	function makeServerTrackingRequest(source = 'facebook', event = 'pageview', data = {}) {
		fetch(['/api/tracking', source, event].join('/'), {
			method: 'post',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify(data)
		});
	}

	function consumeQueue() {
		let removeFromQueue = [];

		if(queue.length === 0 || isGoogleBot()) {
			return;
		}

		queue.forEach((event) => {
			if(!confirmEventDependency(event.dependency, event.values)) {
				return;
			}

			let eventId = event.id;
			makeServerTrackingRequest(event.service, event.endpoint, event.data);

			removeFromQueue.push(eventId);
		});

		queue = queue.filter((event) => {
			return removeFromQueue.indexOf(event.id) < 0;
		});
	}

	function addToQueue(item) {
		queue.push({
			id: item.id,
			dependency: item.dependency,
			values: item.values,
			service: item.service,
			endpoint: item.endpoint,
			data: item.data
		});

		window.requestAnimationFrame(() => {
			consumeQueue();
		});
	}

	function generateEventId() {
		return uuidv4();
	}

	function isGoogleBot() {
		let userAgent = navigator.userAgent;

		return userAgent.toLowerCase().indexOf("googlebot") >= 0;
	}

	/**
	 *
	 * @param event{UpdatedCartEvent|AddToCartEvent|RemoveFromCartEvent}
	 */
	function cartHasUpdated(event) {
		// console.log('cart has update detail:', event.type, event.detail);
		context.actions.cartUpdated(event.detail.lineItems, {id: 'cart_updated', name: 'cart_updated', location: router.asPath});
	}


	function isInStoreDisplay() {
        return typeof window !== 'undefined' && window?.navigator?.userAgent?.indexOf('NEWPORT_INSTOREBROWSER') < 0 ? false : true;
    }

	function collectUTMTags() {
		let url = new URL(window.location.href);
		const urlTags = ['utm_source', 'utm_medium', 'utm_campaign'];

		if(url.searchParams.get(urlTags[0]) || url.searchParams.get(urlTags[1]) | url.searchParams.get(urlTags[2])) {
			let trackingData = {
				source: url.searchParams.get(urlTags[0]),
				medium: url.searchParams.get(urlTags[1]),
				campaign: url.searchParams.get(urlTags[2])
			}

			sessionStorage.setItem('trackingData', JSON.stringify(trackingData));
		}
	}

	const context = useMemo(() => {
		return {
			session: session,
			trackingInitiated,
			actions: {
				init() {
					/* Listen to Cookie Bot event */
					addEventListener('CookiebotOnLoad', (event) => {
						collectUTMTags();
						setTrackingInitiated(true);
					});

					if (router?.basePath?.includes('newport.int')) {
						collectUTMTags();

						setTrackingInitiated(true);

						TagManager.dataLayer({
							dataLayer: {
								domain_country: config.language.languageCode.split('-')[1],
								event: 'tracking_init'
							}
						});
					}

					this.initTrackingScript();
					this.handleSoftLogin();
				},

				initTrackingScript() {
					/* Init TagManager */
					TagManager.initialize(tagManagerArgs);
				},

				pageImpression(title, pageType = undefined) {
					let eventId = generateEventId();

                    const userInfo = {}
                    if (getDiscoveryKey()) {
                        userInfo.vtid_ref = getContactId();
                    }

					/* TagManager */
					TagManager.dataLayer({dataLayer: {ecommerce: null}})
					TagManager.dataLayer({
                        dataLayer: {
                            event: 'page_impression',
                            event_id: eventId,
                            page_info: {
                                url: document.URL,
                                virtual_url: document.baseURI.split('?')[0],
                                page_path: router.asPath.split('?')[0],
                                page_title: title,
                                page_type: convertToSnakeCase(pageType),
                                page_referrer: window?.__navigationReferrer || '',
                                market: config.language.languageCode.split('-')[1],
                                locale: config.language.languageCode,
                                currency: config.currency,
                                trigger_type: navigationType(),
								is_production: !isDevelopment,
                            },
                            ecommerce: {
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
							},
                            user_info: userInfo,
                        },
                    });

					/* Add to async dependency queue */
					/*if (context.session.config.fbPixelId) {
						addToQueue({
							id: eventId,
							dependency: 'cookie',
							values: ['_fbp', process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE],
							service: 'facebook',
							endpoint: 'pageview',
							data: {
								eventId: eventId,
								email: eclubData?.email || undefined,
							}
						});
					}*/
				},

				click({type, text, identifier=null}) {
					let eventId = generateEventId();
					TagManager.dataLayer({dataLayer: {ecommerce: null}})
					TagManager.dataLayer({
						dataLayer: {
							event: 'click_interaction',
							event_id: eventId,
							click: {
								identifier: convertToSnakeCase(identifier || router.asPath),
								text: convertToSnakeCase(text),
								type: convertToSnakeCase(type),
							}
						}
					});
				},

				addToCart({id, price, brand='', list, quantity, couponcode=null, analytics=null}) {
					let eventId = generateEventId();
					let item = {
						item_id: id,
						item_name: convertToSnakeCase(analytics?.item_name),
						index: 0,
						coupon: convertToSnakeCase(couponcode || ''),
						item_brand: convertToSnakeCase(analytics?.item_brand) || '',
						price: price,
						quantity: quantity,
						item_category: convertToSnakeCase(analytics?.item_category) || '',
						item_category2: convertToSnakeCase(analytics?.item_category2)|| '',
						item_category3: convertToSnakeCase(analytics?.item_category3) || '',
						item_category4: convertToSnakeCase(analytics?.item_category4) || '',
						item_category5: convertToSnakeCase(analytics?.item_category5) || '',
					}

					if (list) {
						item['item_list_id'] = list?.id || ''
						item['item_list_name'] = convertToSnakeCase(list?.name) || ''
						item['item_list_location'] = list?.location || ''
					}

					TagManager.dataLayer({dataLayer: { ecommerce: null }})
					let tempLayer = {
						dataLayer: {
							event: 'add_to_cart',
							event_id: eventId,
							ecommerce: {
								value: price * quantity,
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								items: [item],
							}
						}
					};

					if (list) {
						tempLayer.dataLayer.ecommerce['item_list_id'] = list?.id || ''
						tempLayer.dataLayer.ecommerce['item_list_name'] = convertToSnakeCase(list?.name || '')
						tempLayer.dataLayer.ecommerce['item_list_location'] = list?.location || ''
					}

					TagManager.dataLayer(tempLayer);

					/* Make server tracking request */
					/*if (context.session.config.fbPixelId) {
						addToQueue({
							id: eventId,
							dependency: false,
							service: 'facebook',
							endpoint: 'addtocart',
							data: {
								eventId: eventId,
								currency: this.getCurrency(),
								email: eclubData?.email || undefined,
								product: {
									id: id,
									quantity: quantity,
									price: price
								}
							}
						});
					}*/
				},

				removeFromCart({id, price, list, quantity, couponcode=null, analytics=null}) {
					let eventId = generateEventId();
					let item = {
						item_id: id,
						item_name: convertToSnakeCase(analytics?.item_name),
						index: 0,
						coupon: convertToSnakeCase(couponcode || ''),
						item_brand: convertToSnakeCase(analytics?.item_brand) || '',
						price: price,
						quantity: quantity,
						item_category: convertToSnakeCase(analytics?.item_category) || '',
						item_category2: convertToSnakeCase(analytics?.item_category2)|| '',
						item_category3: convertToSnakeCase(analytics?.item_category3) || '',
						item_category4: convertToSnakeCase(analytics?.item_category4) || '',
						item_category5: convertToSnakeCase(analytics?.item_category5) || '',
					}

					if (list) {
						item['item_list_id'] = list?.id || ''
						item['item_list_name'] = convertToSnakeCase(list?.name) || ''
						item['item_list_location'] = list?.location || ''
					}

					TagManager.dataLayer({dataLayer: { ecommerce: null }})
					let tempLayer = {
						dataLayer: {
							event: 'remove_from_cart',
							event_id: eventId,
							ecommerce: {
								value: price * quantity,
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								items: [item],
							}
						}
					};

					if (list) {
						tempLayer.dataLayer.ecommerce['item_list_id'] = list?.id || ''
						tempLayer.dataLayer.ecommerce['item_list_name'] = convertToSnakeCase(list?.name || '')
						tempLayer.dataLayer.ecommerce['item_list_location'] = list?.location || ''
					}

					TagManager.dataLayer(tempLayer);

				},


				async cartUpdated(lineItems, list=null) {
					let eventId = generateEventId();

					let items = await Promise.all(lineItems.filter((item) => {
						return item.type !== 'promotion';
					}).map(async(item, index) => {
						item['analytics'] = await getProductAnalytics(item.referencedId)

						const newItem = {
							item_id: item.payload.productNumber,
							item_name: convertToSnakeCase(item.analytics?.item_name),
							index: index,
							coupon: convertToSnakeCase(cart.campaignCode || ''),
							item_brand: convertToSnakeCase(item.analytics?.item_brand) || '',
							price: item.price.unitPrice,
							quantity: item.quantity,
							item_category: convertToSnakeCase(item.analytics?.item_category) || '',
							item_category2: convertToSnakeCase(item.analytics?.item_category2)|| '',
							item_category3: convertToSnakeCase(item.analytics?.item_category3) || '',
							item_category4: convertToSnakeCase(item.analytics?.item_category4) || '',
							item_category5: convertToSnakeCase(item.analytics?.item_category5) || '',
						}

						if (list) {
							newItem['item_list_id'] = list?.id || ''
							newItem['item_list_name'] = convertToSnakeCase(list?.name) || ''
							newItem['item_list_location'] = list?.location || ''
						}

						return newItem
					})
					);


					TagManager.dataLayer({dataLayer: { ecommerce: null }})
					let tempLayer = {
						dataLayer: {
							event: 'cart_updated',
							event_id: eventId,
							ecommerce: {
								value: cart.price.totalPrice,
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								items: items,
							}
						}
					};


					if (list) {
						tempLayer.dataLayer.ecommerce['item_list_id'] = list?.id || ''
						tempLayer.dataLayer.ecommerce['item_list_name'] = convertToSnakeCase(list?.name || '')
						tempLayer.dataLayer.ecommerce['item_list_location'] = list?.location || ''
					}
					TagManager.dataLayer(tempLayer);
				},

				productImpression({id, analytics, price, brand, discount, list={}}) {
					let eventId = generateEventId();
					TagManager.dataLayer({dataLayer: { ecommerce: null }})

					let item = {
						item_id: id,
						item_name: convertToSnakeCase(analytics?.item_name),
						index: 0,
						item_brand: convertToSnakeCase(brand || '') ,
						price,
						quantity: 1,
						item_category: convertToSnakeCase(analytics?.item_category) || '',
						item_category2: convertToSnakeCase(analytics?.item_category2)|| '',
						item_category3: convertToSnakeCase(analytics?.item_category3) || '',
						item_category4: convertToSnakeCase(analytics?.item_category4) || '',
						item_category5: convertToSnakeCase(analytics?.item_category5) || '',
						item_list_id: list?.id || '',
						item_list_name: convertToSnakeCase(list.name || ''),
						item_list_location: list?.location || '',
						discount: parseFloat(discount) || 0,
					}

					let tempLayer = {
						dataLayer: {
							event: 'product_list_impression',
							event_id: eventId,
							ecommerce: {
								item_list_id: list?.id || '',
								item_list_name: convertToSnakeCase(list.name || ''),
								item_list_location: list?.location || '',
								value: price,
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								items: [item],
							}
						}
					};

					TagManager.dataLayer(tempLayer);
				},

				productClick({id, price, discount, coupon, brand, list, analytics}) {
					let eventId = generateEventId();
					TagManager.dataLayer({dataLayer: {ecommerce: null}})

					let item = {
						item_id: id,
						item_name: convertToSnakeCase(analytics?.item_name),
						index: 0,
						coupon: coupon || '',
						item_brand: convertToSnakeCase(brand || '') ,
						item_category: convertToSnakeCase(analytics?.item_category) || '',
						item_category2: convertToSnakeCase(analytics?.item_category2)|| '',
						item_category3: convertToSnakeCase(analytics?.item_category3) || '',
						item_category4: convertToSnakeCase(analytics?.item_category4) || '',
						item_category5: convertToSnakeCase(analytics?.item_category5) || '',
						item_list_id: list?.id || '',
						item_list_name: convertToSnakeCase(list.name || ''),
						item_list_location: list?.location || '',
						discount: parseFloat(discount) || 0,
						price,
						quantity: 1,
					}

					let tempLayer = {
						dataLayer: {
							event: 'select_product',
							event_id: eventId,
							ecommerce: {
								item_list_id: list?.id || '',
								item_list_name: convertToSnakeCase(list.name || ''),
								item_list_location: list?.location || '',
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								items: [item],
							}
						}
					};

					TagManager.dataLayer(tempLayer);

				},

				productDetails({id, price, discount, brand, coupon, list, analytics}) {
					let eventId = generateEventId();
					TagManager.dataLayer({dataLayer: {ecommerce: null}})

					let item = {
						item_id: id,
						item_name: convertToSnakeCase(analytics?.item_name),
						index: 0,
						coupon: coupon || '',
						item_brand: convertToSnakeCase(brand || '') ,
						item_category: convertToSnakeCase(analytics?.item_category) || '',
						item_category2: convertToSnakeCase(analytics?.item_category2)|| '',
						item_category3: convertToSnakeCase(analytics?.item_category3) || '',
						item_category4: convertToSnakeCase(analytics?.item_category4) || '',
						item_category5: convertToSnakeCase(analytics?.item_category5) || '',
						item_list_id: list?.id || '',
						item_list_name: convertToSnakeCase(list.name || ''),
						item_list_location: list?.location || '',
						discount: parseFloat(discount) || 0,
						price,
						quantity: 1,
					}

					let tempLayer = {
						dataLayer: {
							event: 'product_detail_impression',
							event_id: eventId,
							ecommerce: {
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								value: price,
								items: [item],
							}
						}
					};

					TagManager.dataLayer(tempLayer);

					/* Make server tracking request */
					/*if (context.session.config.fbPixelId) {
						addToQueue({
							id: eventId,
							dependency: 'cookie',
							values: ['_fbp', process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE],
							service: 'facebook',
							endpoint: 'viewcontent',
							data: {
								eventId: eventId,
								currency: this.getCurrency(),
								email: eclubData?.email || undefined,
								product: {
									id: id,
									price: price
								}
							}
						});
					}*/
				},

				/*productListing(categoryId, productIds) {
					let eventId = generateEventId();

					TagManager.dataLayer({
						dataLayer: {
							event: 'product_listing',
							event_id: eventId,
							domain_country: config.language.languageCode.split('-')[1],
							ecommerce: {
								category_id: categoryId,
								listing: {
									productIds
								}
							}
						}
					});
				},*/

				checkoutCart({cartItems, value, coupon}) {
					TagManager.dataLayer({dataLayer: {ecommerce: null}})
					let eventId = generateEventId();

					let items = cartItems.map((item, index) => {
						return {
							item_id: item.item_number,
							item_name: convertToSnakeCase(item?.analytics?.item_name),
							index: index,
							coupon: coupon || '',
							item_brand: convertToSnakeCase(item?.analytics?.item_brand || '') ,
							item_category: convertToSnakeCase(item?.analytics?.item_category) || '',
							item_category2: convertToSnakeCase(item?.analytics?.item_category2)|| '',
							item_category3: convertToSnakeCase(item?.analytics?.item_category3) || '',
							item_category4: convertToSnakeCase(item?.analytics?.item_category4) || '',
							item_category5: convertToSnakeCase(item?.analytics?.item_category5) || '',
							discount: item.discount || 0,
							price: item.price,
							quantity: item.quantity,
						}
					});

					let tempLayer = {
						dataLayer: {
							event: 'checkout',
							event_id: eventId,
							ecommerce: {
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								value: value,
								items: items,
							}
						}
					};

					TagManager.dataLayer(tempLayer);
				},

				purchase({order}) {
					TagManager.dataLayer({dataLayer: {ecommerce: null}})
					let eventId = generateEventId();
					let promoCode = null;


					let items = order.lineItems.filter((item) => {
						if(item.type === 'promotion') {
							promoCode = item?.payload?.code;
						}
						return item.type !== 'promotion';
					}).map((item, index) => {

						let itemPrice = getCartItemPrices(item);
						return {
							item_id: item.payload.productNumber,
							item_name: convertToSnakeCase(item?.analytics?.item_name),
							index: index,
							coupon: promoCode || '',
							item_brand: convertToSnakeCase(item?.analytics?.item_brand || '') ,
							item_category: convertToSnakeCase(item?.analytics?.item_category) || '',
							item_category2: convertToSnakeCase(item?.analytics?.item_category2)|| '',
							item_category3: convertToSnakeCase(item?.analytics?.item_category3) || '',
							item_category4: convertToSnakeCase(item?.analytics?.item_category4) || '',
							item_category5: convertToSnakeCase(item?.analytics?.item_category5) || '',
							discount: item.discount || 0,
							price: itemPrice.price,
							quantity: item?.quantity,
						}
					});
					let tempLayer = {
						dataLayer: {
							event: 'purchase',
							event_id: eventId,
							user_info: {
								first_name: order.orderCustomer?.firstName || '',
								last_name: order.orderCustomer?.lastName || '',
								email: order.orderCustomer?.email || '',
								sha256_hashed_email: md5(order.orderCustomer?.email || ''),
								phone_number: order.addresses?.[0].phoneNumber || '',
								sha256_hashed_phone: order.addresses?.[0].phoneNumber ? md5(order.addresses?.[0].phoneNumber) : '',
								city: order.addresses?.[0]?.city || '',
								region: order.addresses?.[0]?.countryState || '',
								postal_code: order.addresses?.[0]?.zipcode || '',
								street: order.addresses?.[0]?.street || '',
								country: order.addresses?.[0]?.country?.name || '',
                                vtid_ref: getContactId() || getDiscoveryKey() || ''
							},
							ecommerce: {
								transaction_id: order.orderNumber,
								value: order.price?.totalPrice,
								net_value: order.price?.netPrice,
								net_value_without_shipping: order?.price?.netPrice - (order?.shippingTotal - order?.shippingCosts?.calculatedTaxes?.[0]?.tax),
								tax: order.price?.totalPrice - order.price?.netPrice,
								shipping: order?.shippingTotal,
								coupon: promoCode || '',
								shipping_tier: order?.deliveries?.[0]?.shippingMethod?.technicalName,
								payment_type: order?.transactions?.[0]?.paymentMethod?.distinguishableName || '',
								cart_ref: Cookies.get(process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE),
								items: items,
							}
						}
					};

					TagManager.dataLayer(tempLayer);


					// Facebook backend tracking
					/*if (context.session.config.fbPixelId) {
						addToQueue({
							id: eventId,
							dependency: 'cookie',
							values: ['_fbp', process.env.NEXT_PUBLIC_STOREFRONT_SESSION_COOKIE],
							service: 'facebook',
							endpoint: 'purchase',
							data: {
								eventId: eventId,
								currency: order.currency.isoCode,
								products: order.lineItems.filter((item) => {
									return item.type !== 'promotion';
								}).map((item) => {
									return {
										id: item.payload?.productNumber,
										price: item.price?.unitPrice,
										quantity: item.quantity
									}
								}),
								value: order.price?.totalPrice,
								email: order.orderCustomer?.email,
								dateOfBirth: order.orderCustomer?.customer?.birthday ? order.orderCustomer?.customer?.birthday?.split('T')[0].split('-').join('') : undefined,
								firstName: order.orderCustomer?.firstName
							}
						});
					}*/

					// Pinterest Conversion API Tracking
					/*if(config.language.locale === 'sv') {
						let eventTime = Math.floor(Date.now() / 1000);
						addToQueue({
							id: eventId,
							service: 'pinterest',
							endpoint: 'purchase',
							data: {
								eventId: eventId,
								eventTime: eventTime,
								market: 'SE',
								user_data: {
									em: order.orderCustomer?.email,
								},
								custom_data: {
									currency: order.currency.isoCode,
									value: '' + order.price?.totalPrice,
									content_ids: order.lineItems.filter((item) => {
										return item.type !== 'promotion';
									}).map((item) => {
										return item.payload?.productNumber
									}),
								}
							}
						});
					}*/

					/**
					 * @see /api/tracking/voyado/purchase
					 */
					addToQueue({
						id: eventId,
						service: 'voyado',
						endpoint: 'purchase',
						data: {
							market: session?.config?.elevateMarket,
							customer: {
								sessionKey: getUserSession()?.sessionKey,
								customerKey: getDiscoveryKey(),
							},
							lines: order.lineItems.filter((item) => {
								return item.type !== 'promotion';
							}).map((item) => {
								return {
									variantKey: item.payload?.productNumber,
									sellingPrice: item.price?.unitPrice,
									quantity: item.quantity
								}
							}),
						}
					});
				},

				experiment(experiment, result) {
					let eventId = generateEventId();
					let eventTime = Math.floor(Date.now() / 1000);
					/* TagManager */
					TagManager.dataLayer({
						dataLayer: {
							event: 'experiment',
							event_id: eventId,
							experiment_id: experiment.name,
							result_id: result.featureId,
							variation_id: result.variationId,
						}
					});
				},

				async sendUTMTags() {
					let trackingData;

					try {
						trackingData = sessionStorage.getItem('trackingData') ? JSON.parse(sessionStorage.getItem('trackingData')) : false;
					} catch (e) {
						console.log(e);
					}

					if(trackingData) {
						await fetch('/api/tracking/utm-tags', {
							method: 'POST',
							headers: {
								'Accept': 'application/json',
								'Content-Type': 'application/json',
							},
							credentials: 'include',
							body: JSON.stringify({trackingData})
						});
					}
				},

				async sendDiscoveryKey() {
					let discoveryKey = myNewportService?.getLocalDiscoveryKey();

					if(discoveryKey) {
						await fetch('/api/tracking/custom-fields', {
							method: 'POST',
							headers: {
								'Accept': 'application/json',
								'Content-Type': 'application/json',
							},
							credentials: 'include',
							body: JSON.stringify({ customFields: { discovery_key: discoveryKey } })
						});
					}
				},

				removeUTMTags() {
					sessionStorage.getItem('trackingData') ? sessionStorage.removeItem('trackingData') : false;
				},

				getEclubString() {
					const queryString = window.location.search;
					const urlParams = new URLSearchParams(queryString);

					return urlParams.get('eclub');
				},

				getCurrency() {
					return context.session.config.currency;
				},

				async handleSoftLogin() {
					const eclubString = this.getEclubString();

					if(eclubString) {
						try {
							const eclubResponse = await fetch('/api/voyado/eclub', {
								method: 'POST',
								body: JSON.stringify({
									eclubString
								})
							});

							let eclubResponseJson = await eclubResponse.json();

							if(eclubResponseJson.status === 'success') {
								let eclubData = eclubResponseJson.data;
								setEclubData(eclubData);
								localStorage.setItem('eclubData', JSON.stringify(eclubData));

								//Dispatch Eclub login event
								document.dispatchEvent(new CustomEvent("eclub-login"));
							}
						} catch (e) {

						}
					} else {
						//Dispatch Eclub login event
						setTimeout(() => {
							document.dispatchEvent(new CustomEvent("eclub-missing"));
						}, 100);
					}
				}
			},
		};
	}, [session, trackingInitiated]);


	/**
	 * Initialize tracking
	 */
	useEffect(() => {
		if(session.token && !isGoogleBot()) {
			context.actions.init();
		}
	}, [session.token]);

	/**
	 * Cookie listeners
	 */
	useEffect(() => {
		let waitCount = 0;
		(function wait() {
			if(Cookies.get('_fbp')) {
				consumeQueue();
			} else {
				if(waitCount < 20) {
					setTimeout(wait, 250);
				}

				waitCount++;
			}
		})();
	}, []);

	/**
	 * Event listeners
	 */
	useEffect(() => {
		if(session.token) {
			document.addEventListener("add-to-cart", cartHasUpdated);
			document.addEventListener("has-updated-cart", cartHasUpdated);
			document.addEventListener("remove-from-cart", cartHasUpdated);
		}

		return () => {
			document.removeEventListener('add-to-cart', cartHasUpdated);
			document.removeEventListener('has-updated-cart', cartHasUpdated);
			document.removeEventListener('remove-from-cart', cartHasUpdated);
		}
	}, []);

	return <TrackingContext.Provider value={context}>
		{!isInStoreDisplay() ? <Head>
			<script data-cookieconsent="ignore" dangerouslySetInnerHTML={{__html: `
					window.dataLayer = window.dataLayer || [];
					
					function gtag() {
						dataLayer.push(arguments);
					}
					
					gtag("consent", "default", {
						ad_personalization: "denied",
						ad_storage: "denied",
						ad_user_data: "denied",
						analytics_storage: "denied",
						functionality_storage: "denied",
						personalization_storage: "denied",
						security_storage: "granted",
						wait_for_update: 500,
					});
					
					gtag("set", "ads_data_redaction", true);
				`}}/>
			<script
			  id="Cookiebot"
			  src="https://consent.cookiebot.com/uc.js"
			  data-cbid="40c1ea95-4502-4abf-b287-4d10db7f8517"
			  type="text/javascript"
			  data-consentmode-defaults="disabled"
			/>
		</Head> : ''}
		{children}
	</TrackingContext.Provider>;
}
